import React, { useEffect, useState, useMemo, useCallback } from "react";
import axios from "axios";
import { Chart } from "react-google-charts";
import { useTable, useFilters } from "react-table";
import "../styles/Dashboard.css";

const API_BASE_URL = process.env.REACT_APP_API_URL || "https://depotaircerdas.com/api";

const MonthlyReport = ({ username }) => {
    const currentYear = new Date().getFullYear();
    const [selectedYear, setSelectedYear] = useState(currentYear);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const yearOptions = useMemo(() => {
        return Array.from({ length: 11 }, (_, i) => currentYear - 5 + i);
    }, [currentYear]);

    const getToken = () => {
        const token = localStorage.getItem("token");
        return token ? `Bearer ${token}` : null;
    };

    const fetchData = useCallback(async () => {
        const token = getToken();
        if (!token) {
            window.location.href = "/login";
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const res = await axios.get(
                `${API_BASE_URL}/reports/${username}/monthly?year=${selectedYear}`,
                {
                    headers: {
                        Authorization: token,
                        "Content-Type": "application/json",
                    },
                }
            );
            setData(res.data);
        } catch (err) {
            console.error("❌ Error fetching monthly data:", err);
            setError("Gagal mengambil data bulanan.");
        } finally {
            setLoading(false);
        }
    }, [username, selectedYear]);

    useEffect(() => {
        fetchData();
        const interval = setInterval(fetchData, 60000);
        return () => clearInterval(interval);
    }, [fetchData]);

    const groupedData = useMemo(() => {
        if (!data?.monthly) return [];

        return data.monthly
            .map((d) => {
                const monthDate = new Date(`${d.year}-${String(d.month).padStart(2, "0")}-01T00:00:00`);
                return {
                    monthNumber: Number(d.month),
                    month: monthDate.toLocaleDateString("id-ID", {
                        month: "long",
                        year: "numeric",
                    }),
                    volume: parseFloat(d.volume) || 0,
                    gallons: parseFloat(d.gallons) || 0,
                };
            })
            .sort((a, b) => a.monthNumber - b.monthNumber);
    }, [data]);

    const isDataEmpty = groupedData.length === 0;

    const summary = useMemo(() => {
        const totalVolume = groupedData.reduce((sum, item) => sum + item.volume, 0);
        const totalGallons = groupedData.reduce((sum, item) => sum + item.gallons, 0);
        return {
            totalVolume,
            totalGallons,
            totalMonths: groupedData.length,
        };
    }, [groupedData]);

    const columns = useMemo(
        () => [
            { Header: "Bulan", accessor: "month" },
            {
                Header: "Volume (L)",
                accessor: "volume",
                Cell: ({ value }) => Number(value).toFixed(2),
            },
            { Header: "Total Galon", accessor: "gallons" },
        ],
        []
    );

    const tableInstance = useTable({ columns, data: groupedData }, useFilters);
    const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = tableInstance;

    return (
        <div className="report-card-shell">
            <div className="report-topbar">
                <div>
                    <h3 className="report-title">Laporan Bulanan</h3>
                    <p className="report-subtitle">
                        Ringkasan penggunaan air per bulan untuk tahun {selectedYear}.
                    </p>
                </div>

                <button className="refresh-btn" onClick={fetchData}>
                    Refresh Data
                </button>
            </div>

            <div className="report-filters">
                <div className="form-group">
                    <label>Tahun</label>
                    <select
                        className="report-select"
                        value={selectedYear}
                        onChange={(e) => setSelectedYear(Number(e.target.value))}
                    >
                        {yearOptions.map((year) => (
                            <option key={year} value={year}>
                                {year}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {loading ? (
                <p className="loading">Memuat data...</p>
            ) : error ? (
                <p className="error-message">{error}</p>
            ) : isDataEmpty ? (
                <p className="report-empty">Belum ada data bulanan untuk tahun {selectedYear}.</p>
            ) : (
                <>
                    <div className="report-summary-grid">
                        <div className="report-summary-card">
                            <span className="summary-label">Total Volume</span>
                            <strong className="summary-value">
                                {summary.totalVolume.toFixed(2)} L
                            </strong>
                            <small className="summary-subvalue">Tahun {selectedYear}</small>
                        </div>

                        <div className="report-summary-card">
                            <span className="summary-label">Total Galon</span>
                            <strong className="summary-value">{summary.totalGallons}</strong>
                            <small className="summary-subvalue">Akumulasi seluruh bulan</small>
                        </div>

                        <div className="report-summary-card">
                            <span className="summary-label">Jumlah Bulan</span>
                            <strong className="summary-value">{summary.totalMonths}</strong>
                            <small className="summary-subvalue">Bulan yang memiliki data</small>
                        </div>
                    </div>

                    <div className="chart-container">
                        <Chart
                            chartType="LineChart"
                            width="100%"
                            height="360px"
                            data={[
                                ["Bulan", "Volume (L)", "Galon"],
                                ...groupedData.map((d) => [d.month, Number(d.volume), Number(d.gallons)]),
                            ]}
                            options={{
                                title: `Trend Penggunaan Air Bulanan - ${selectedYear}`,
                                curveType: "function",
                                legend: { position: "top" },
                                colors: ["#0ea5e9", "#22c55e"],
                                series: {
                                    0: { targetAxisIndex: 0 },
                                    1: { targetAxisIndex: 1 },
                                },
                                vAxes: {
                                    0: { title: "Volume (L)" },
                                    1: { title: "Galon" },
                                },
                                chartArea: {
                                    left: 55,
                                    top: 55,
                                    width: "88%",
                                    height: "72%",
                                },
                            }}
                                                    />
                    </div>

                    <div className="dashboard-table-wrapper">
                        <table {...getTableProps()} className="dashboard-table">
                            <thead>
                                {headerGroups.map((headerGroup) => (
                                    <tr {...headerGroup.getHeaderGroupProps()} key={headerGroup.id}>
                                        {headerGroup.headers.map((column) => (
                                            <th {...column.getHeaderProps()} key={column.id}>
                                                {column.render("Header")}
                                            </th>
                                        ))}
                                    </tr>
                                ))}
                            </thead>
                            <tbody {...getTableBodyProps()}>
                                {rows.map((row) => {
                                    prepareRow(row);
                                    return (
                                        <tr {...row.getRowProps()} key={row.id}>
                                            {row.cells.map((cell) => (
                                                <td {...cell.getCellProps()} key={cell.column.id}>
                                                    {cell.render("Cell")}
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </>
            )}
        </div>
    );
};

export default MonthlyReport;